import { FreeSpinsInfoEvent, SpinResponse } from '@cgs/data-access';
import { Container, IntervalAction } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { GameStateMachine } from '../../../../reels_engine/state_machine/game-state-machine';
import {
  T_IFreeSpinsModeProvider,
  T_IFreeSpinsPopupViewUpdater,
  T_ISpinParams,
} from '../../../../type_definitions';
import { IFreeSpinsModeProvider } from '../../free_spins/i_free_spins_mode_provider';
import { IFreeSpinsPopupViewUpdater } from '../../free_spins/i_free_spins_popup_view_updater';
import { StartFreeSpinsPopupView } from './start-free-spins-popup.view';
import { BaseSlotPopupController } from '../../../common/slot/controllers/base_popup_controller';
import { GameStateMachineStates } from '../../../../reels_engine/state_machine/game-state-machine-states';
import {
  START_FREE_SPINS_DEFAULT_OPTIONS,
  StartFreeSpinsPopupOptions,
} from './start-free-spins-popup.typings';
import { AwaitableAction } from '../../../../reels_engine/actions/awaitable_action';
import { ISpinParams } from '../../../../reels_engine/game_components/i_spin_params';

export class StartFreeSpinsPopupController extends BaseSlotPopupController<StartFreeSpinsPopupView> {
  protected readonly _gameStateMachine: GameStateMachine<SpinResponse>;
  protected readonly _options: StartFreeSpinsPopupOptions;
  protected readonly _spinParams: ISpinParams;

  constructor(
    container: Container,
    popupView: StartFreeSpinsPopupView,
    partialOptions?: StartFreeSpinsPopupOptions
  ) {
    super(container, popupView, false);

    this._gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    this._options = { ...START_FREE_SPINS_DEFAULT_OPTIONS, ...partialOptions };
    this._spinParams = container.resolve<ISpinParams>(T_ISpinParams);

    if (this._options.showRecovery) {
      this._gameStateMachine.freeSpinsRecovery.appendLazyAnimation(() => this.recoveryAction());
    }
  }

  public onPopupClosed(): void {
    if (
      this._gameStateMachine.curResponse.freeSpinsInfo &&
      this.slotSoundController &&
      ((this._options.stopBackgroundSoundOnStart &&
        this._gameStateMachine.curResponse.freeSpinsInfo.event ==
          FreeSpinsInfoEvent.FreeSpinsStarted) ||
        (this._options.stopBackgroundSoundOnAdd &&
          this._gameStateMachine.curResponse.freeSpinsInfo.event ==
            FreeSpinsInfoEvent.FreeSpinsAdded))
    ) {
      this.slotSoundController.playBackgroundSound();
    }

    this.gameStateMachineNotifier.notifier.notifyListenersExited('popup');
    this.slotPopupCoordinator.onPopupHidden(this.view.popupId);

    this.gameStateMachineNotifier.gameStateMachine.doSpin();
  }

  public onPopupShowing(): void {
    if (
      this._gameStateMachine.curResponse.freeSpinsInfo &&
      this.slotSoundController &&
      ((this._options.stopBackgroundSoundOnStart &&
        this._gameStateMachine.curResponse.freeSpinsInfo.event ==
          FreeSpinsInfoEvent.FreeSpinsStarted) ||
        (this._options.stopBackgroundSoundOnAdd &&
          this._gameStateMachine.curResponse.freeSpinsInfo.event ==
            FreeSpinsInfoEvent.FreeSpinsAdded))
    ) {
      this.slotSoundController.stopBackgroundSound();
    }

    this.gameStateMachineNotifier.notifier.notifyListenersEntered('popup');
    this.slotPopupCoordinator.onPopupShown(this.view.popupId);
  }

  public onAnimCompleted(): void {
    this.view.hide();
  }

  public onStateEntered(slotState: string): void {
    switch (slotState) {
      case GameStateMachineStates.BeginFreeSpinsPopup:
        const freeSpinsInfo = this._gameStateMachine.curResponse.freeSpinsInfo!;

        if (
          freeSpinsInfo.event === FreeSpinsInfoEvent.FreeSpinsAdded &&
          !this._options.showFreeSpinsAdd
        ) {
          this.gameStateMachineNotifier.gameStateMachine.doSpin();
          break;
        }

        this.view.show();
        this.view.setFreeSpins(this.getFreeSpinsAdded());
        this.view.postEvent('default');

        const param =
          freeSpinsInfo.event == FreeSpinsInfoEvent.FreeSpinsStarted
            ? this._options.freeSpinsStateName!
            : this._options.freeSpinsAddStateName!;

        if (!this._options.useFreeSpinMode) {
          this.view.postEvent(param);
        } else {
          const modeProvider =
            this.container.resolve<IFreeSpinsModeProvider>(T_IFreeSpinsModeProvider);
          if (modeProvider) {
            const stateName = param + '_' + modeProvider.currentMode;
            this.view.postEvent(stateName);
          }
        }

        if (this._options.useViewUpdater) {
          const viewUpdater = this.container.resolve<IFreeSpinsPopupViewUpdater>(
            T_IFreeSpinsPopupViewUpdater,
            { optional: true }
          );
          if (viewUpdater) {
            viewUpdater.updateViewBeforeShowingPopup(this.view.view);
          }
        }

        if (this._spinParams.skipPopups) {
          this.view.startActivator();
        }

        break;
      default:
        break;
    }
  }

  protected getFreeSpinsAdded(): number {
    return this._gameStateMachine.curResponse.freeSpinsInfo?.freeSpinsAdded || 0;
  }

  protected recoveryAction(): IntervalAction {
    this.view.show();

    const freeSpinsCount = this._gameStateMachine.curResponse.freeSpinsInfo!.freeSpinGroups!.reduce(
      (result, group) => result + group.count!,
      0
    );

    this.view.setFreeSpins(freeSpinsCount);
    this.view.postEvent(this._options.freeSpinsRecoveryStateName!);

    if (this._spinParams.skipPopups) {
      this.view.startActivator();
    }

    return new AwaitableAction(this.slotPopupCoordinator.popupHidden.first);
  }
}
